import { UserProgress } from '@zeus/index'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useListSearch } from '~/hooks/use-list-search'

/**
 * Valide une valeur d'avancement venue de l'URL (#201) ou du sélecteur.
 *
 * Une chaîne arbitraire partirait telle quelle dans l'enum GraphQL, qui la
 * rejetterait — et la page s'ouvrirait sur une erreur plutôt que sur la liste.
 * Une valeur inconnue vaut donc « aucun filtre ».
 */
export function parseProgress(value: string | null): UserProgress | '' {
  return Object.values(UserProgress).find(progress => progress === value) ?? ''
}

/**
 * Recherche et filtres de la liste des comptes, appliqués côté serveur.
 * `onChange` réagit à tout changement de la tranche affichée (retour en page 1,
 * sélection vidée) et, comme pour `useListSearch`, ne doit faire que des
 * `setState` du composant appelant.
 */
export function useUserFilters(onChange: () => void) {
  const [searchParams] = useSearchParams()
  // Une requête par pause de saisie, pas par frappe (#107).
  const { search, setSearch, query } = useListSearch(onChange)
  // Filtre « groupe » (#135) : un seul groupe à la fois côté UI, même si l'API
  // accepte une liste — un multi-select ici alourdirait la barre pour un besoin
  // (« les apprenants du client A ») qui se pose au singulier.
  const [groupId, setGroupIdState] = useState('')
  // Filtre « avancement » (#137) : la liste de relance manuelle se sort en
  // choisissant « en cours » ou « jamais commencé ».
  //
  // Amorçable par l'URL (`?progress=NOT_STARTED`), parce que l'accueil (#201) y
  // envoie depuis son compteur « jamais commencé » : sans ça, le lien
  // atterrirait sur la liste complète et la personne devrait refaire à la main
  // le tri qu'on venait de lui annoncer. Lu une seule fois, à l'ouverture — la
  // suite appartient au sélecteur, qui ne réécrit pas l'URL.
  const [progress, setProgressState] = useState(() => parseProgress(searchParams.get('progress')))

  const setGroupId = (next: string) => {
    setGroupIdState(next)
    onChange()
  }

  const setProgress = (next: UserProgress | '') => {
    setProgressState(next)
    onChange()
  }

  const reset = () => {
    setSearch('')
    setGroupIdState('')
    setProgressState('')
    onChange()
  }

  const filterBy = groupId || progress
    ? {
        ...(groupId && { group: [groupId] }),
        ...(progress && { progress }),
      }
    : undefined

  return {
    search,
    setSearch,
    query,
    groupId,
    setGroupId,
    progress,
    setProgress,
    filterBy,
    filtering: query !== '' || filterBy !== undefined,
    reset,
  }
}

export type UserFilters = ReturnType<typeof useUserFilters>
